/**
 * Shows the Stamped "write a review" form (#stamped-main-widget
 * .new-review-form) as a modal instead of inline under the button.
 *
 * Stamped's own toggle (StampedFn.toggleForm, wired inline on the
 * "レビューを書く" button) slides the form open by animating inline
 * height/margin/padding/overflow, then rewrites those inline styles when the
 * animation ends — on a fixed modal that both looks odd and fights the modal
 * styles. So the button's click/Enter is intercepted here (capture phase,
 * before the inline handler runs) and the form is shown/hidden directly,
 * with no animation. Submitting still goes through Stamped
 * (form onsubmit → StampedFn.submitForm).
 *
 * The modal look lives in assets/dc-component-stamped-widget.css
 * (.new-review-form.stamped-visible). This script adds the header with a
 * close button, closing on the backdrop / Esc, and the page scroll lock.
 *
 * Also used for the rest of the Stamped widget on the PDP: the star badge's
 * jump to the reviews, the reviews list's search placeholder, YY/MM/DD
 * dates, "+n" on the 4th review photo, the photo strip's prev/next arrows
 * (or, with the Moast reels in its place, the reels' no-loop setting),
 * the thumbnails in the review photo modal and, on mobile, opening a whole
 * review from its card.
 */
(() => {
  const TRIGGER = '#stamped-main-widget .stamped-summary-actions-newreview';
  const FORM = '#stamped-main-widget .new-review-form';
  const CLOSE_ICON =
    '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">' +
    '<path d="M3.6 3.6L14.4 14.4" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/>' +
    '<path d="M14.4 3.6L3.6 14.4" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/></svg>';

  let lastTrigger = null;

  const getForm = () => document.querySelector(FORM);
  const isOpen = (form) => form.classList.contains('stamped-visible') && form.style.display !== 'none';

  function addHeader(form) {
    if (form.querySelector('.dc-review-modal__head')) return;
    const trigger = document.querySelector(TRIGGER);
    const title = (trigger && trigger.textContent.trim()) || 'レビューを書く';

    const head = document.createElement('div');
    head.className = 'dc-review-modal__head';
    head.innerHTML =
      '<p class="dc-review-modal__title"></p>' +
      '<button type="button" class="dc-review-modal__close" aria-label="閉じる">' + CLOSE_ICON + '</button>';
    head.querySelector('.dc-review-modal__title').textContent = title;
    head.querySelector('.dc-review-modal__close').addEventListener('click', () => close(form));
    form.prepend(head);

    form.setAttribute('role', 'dialog');
    form.setAttribute('aria-modal', 'true');
    form.setAttribute('aria-label', title);
  }

  // Clear anything a Stamped slide animation may have left inline.
  function resetInline(form) {
    ['height', 'overflow', 'margin-top', 'margin-bottom', 'padding-top', 'padding-bottom', 'opacity'].forEach((prop) =>
      form.style.removeProperty(prop)
    );
  }

  // Logged-in customer (window.dcReviewer, output by
  // sections/product-information.liquid): fill 名 / Eメール when the form
  // opens — empty fields only, so whatever the customer typed stays.
  function prefill(form) {
    const me = window.dcReviewer;
    if (!me) return;
    [
      ['input[name="author"]', me.name],
      ['input[name="email"]', me.email],
    ].forEach(([selector, value]) => {
      const input = form.querySelector(selector);
      if (!input || input.value.trim() || !value) return;
      input.value = value;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function open(form, trigger) {
    lastTrigger = trigger || null;
    addHeader(form);
    prefill(form);
    resetInline(form);
    form.style.display = 'block';
    form.classList.add('stamped-visible');
    form.setAttribute('aria-expanded', 'true');
    sync(form);
    const first = form.querySelector('input:not([type="hidden"]):not([tabindex="-1"]), textarea');
    if (first) first.focus({ preventScroll: true });
  }

  function close(form) {
    resetInline(form);
    form.style.display = 'none';
    form.classList.remove('stamped-visible');
    form.setAttribute('aria-expanded', 'false');
    sync(form);
    if (lastTrigger) lastTrigger.focus({ preventScroll: true });
  }

  function sync(form) {
    document.documentElement.classList.toggle('dc-review-modal-open', isOpen(form));
  }

  // "レビューを書く": open the modal ourselves; stop the event before it
  // reaches the button's inline Stamped handler.
  function onTrigger(event) {
    const trigger = event.target.closest && event.target.closest(TRIGGER);
    if (!trigger) return false;
    const form = getForm();
    if (!form) return false;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (isOpen(form)) close(form);
    else open(form, trigger);
    return true;
  }

  document.addEventListener(
    'click',
    (event) => {
      if (onTrigger(event)) return;
      // Star badge under the title ("全レビューを見る"): jump to the reviews,
      // stopping below the sticky header. Stamped's own badge click scrolls
      // with no offset (content ended up under the header on mobile), so
      // it's stopped here.
      if (event.target.closest && event.target.closest('.stamped-main-badge')) {
        const widget = document.getElementById('stamped-main-widget');
        if (!widget) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        // With Moast reels the summary line sits above them, outside the
        // widget's box (see dc-component-stamped-widget.css).
        const target = (getReels() && widget.querySelector('.summary-overview')) || widget;
        // scrollIntoView rather than window.scrollTo: on PC the page scrolls
        // inside .page-wrapper, not the window.
        const header = document.getElementById('header-component');
        const headerHeight = header ? header.offsetHeight : 0;
        target.style.scrollMarginTop = headerHeight + 16 + 'px';
        target.scrollIntoView({ block: 'start', behavior: 'smooth' });
        return;
      }
      // Backdrop: the modal's shadow covers the page, so a click that lands
      // outside the open form means the backdrop was clicked.
      const form = getForm();
      if (form && isOpen(form) && !form.contains(event.target)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        close(form);
      }
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        onTrigger(event);
        return;
      }
      if (event.key === 'Escape') {
        const form = getForm();
        if (form && isOpen(form)) close(form);
      }
    },
    true
  );

  // Keep the scroll lock in step if Stamped hides the form itself (e.g.
  // after a successful submit).
  function watch(form) {
    if (form.dataset.dcModal) return;
    form.dataset.dcModal = 'true';
    new MutationObserver(() => sync(form)).observe(form, { attributes: true, attributeFilter: ['class', 'style'] });
  }

  // ---- Reviews list (Figma "Reviews Container") ----
  // Copy Stamped only renders as plain text (no app setting / CSS hook).
  const CHEVRON =
    '<svg viewBox="0 0 15 15" fill="none" aria-hidden="true">' +
    '<path d="M5.75 3.5L9.75 7.5L5.75 11.5" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function localizeCopy() {
    const search = document.querySelector('#stamped-main-widget .stamped-reviews-search-input');
    if (search && search.placeholder !== 'レビューを検索') search.placeholder = 'レビューを検索';
  }

  // Dates as YY/MM/DD (Stamped: YYYY/MM/DD), in the list and the photo modal.
  function formatDates() {
    document.querySelectorAll('#stamped-main-widget .stamped-review .created, .stamped-ugc-modal-date').forEach((el) => {
      const match = el.textContent.trim().match(/^\d{2}(\d{2}\/\d{2}\/\d{2})$/);
      if (match) el.textContent = match[1];
    });
  }

  // Review photos: at most 4 shown (CSS), the 4th marked "+n" for the rest.
  function markReviewPhotos() {
    document.querySelectorAll('#stamped-main-widget .stamped-review .stamped-review-image').forEach((box) => {
      const links = box.querySelectorAll(':scope > a');
      const fourth = links[UGC_MAX_THUMBS - 1];
      const more = links.length > UGC_MAX_THUMBS ? '+' + (links.length - (UGC_MAX_THUMBS - 1)) : null;
      links.forEach((link) => {
        if (link !== fourth && link.dataset.more) delete link.dataset.more;
      });
      if (fourth && more && fourth.dataset.more !== more) fourth.dataset.more = more;
      else if (fourth && !more && fourth.dataset.more) delete fourth.dataset.more;
    });
  }

  // Photo strip: prev/next arrows that scroll it by roughly one view; each
  // arrow hides at its end of the strip (CSS hides both on mobile, where
  // the strip is swiped).
  function addPhotoArrows() {
    const box = document.querySelector('#stamped-main-widget .stamped-summary-photos-container');
    const strip = box && box.querySelector('.stamped-photos-carousel');
    if (!strip || box.querySelector('.dc-photo-strip-arrow')) return;

    const make = (dir) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dc-photo-strip-arrow dc-photo-strip-arrow--' + dir;
      button.setAttribute('aria-label', dir === 'prev' ? '前へ' : '次へ');
      button.innerHTML = CHEVRON;
      if (dir === 'prev') button.querySelector('svg').style.transform = 'scaleX(-1)';
      button.addEventListener('click', () => {
        strip.scrollBy({ left: (dir === 'prev' ? -1 : 1) * strip.clientWidth * 0.8, behavior: 'smooth' });
      });
      box.appendChild(button);
      return button;
    };
    const prev = make('prev');
    const next = make('next');

    const update = () => {
      prev.hidden = strip.scrollLeft <= 1;
      next.hidden = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 1;
    };
    strip.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  // PC: review text capped at 6 lines (Figma: 108px), with "··· もっと表示"
  // placed right where the visible text ends (not pinned to the corner):
  // the collapsed view is the longest prefix of the text that still fits in
  // 6 lines together with the button, found by binary search. Expanding
  // restores Stamped's original markup with "閉じる" after it. If Stamped
  // truncated the text itself (its word-limit "Read more" link), that link
  // is clicked on the first expand so the full text loads.
  // State lives on the review card (data-dc-long / data-dc-expanded) and the
  // original markup in a property on it, since Stamped may rewrite the <p>.
  const MAX_LINES = 6;

  function makeToggle(review, expanded) {
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'dc-review-more' + (expanded ? ' dc-review-more--less' : '');
    toggle.setAttribute('aria-expanded', String(expanded));
    toggle.innerHTML = expanded
      ? '<span class="dc-review-more__label">閉じる</span>'
      : '<span aria-hidden="true">··· </span><span class="dc-review-more__label">もっと表示</span>';
    toggle.addEventListener('click', () => {
      const willExpand = !expanded;
      review.dataset.dcExpanded = String(willExpand);
      renderReview(review);
      if (willExpand) {
        const body = review.querySelector('.stamped-review-content-body');
        const stampedMore = body && body.querySelector('a.stamped-review-read-more');
        if (stampedMore) stampedMore.click();
      } else if (review.getBoundingClientRect().top < 0) {
        // Collapsing a long review can leave the reader below its card.
        review.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    });
    return toggle;
  }

  function maxHeightOf(body) {
    return parseFloat(getComputedStyle(body).lineHeight) * MAX_LINES;
  }

  // Original markup of the body, without our toggle.
  function originalHTML(body) {
    const clone = body.cloneNode(true);
    clone.querySelectorAll('.dc-review-more').forEach((el) => el.remove());
    return clone.innerHTML;
  }

  function renderReview(review) {
    const body = review.querySelector('.stamped-review-content-body');
    if (!body) return;
    const expanded = review.dataset.dcExpanded === 'true';
    const full = review.dcFullHTML;

    if (expanded) {
      body.classList.remove('dc-clamp');
      body.innerHTML = full;
      body.appendChild(makeToggle(review, true));
      body.dataset.dcRendered = 'expanded';
      return;
    }

    // Collapsed: prefix + toggle, as long as it stays within 6 lines.
    const holder = document.createElement('div');
    holder.innerHTML = full;
    holder.querySelectorAll('a.stamped-review-read-more').forEach((el) => el.remove());
    const text = holder.textContent.replace(/\s+$/, '');
    const max = maxHeightOf(body);
    const toggle = makeToggle(review, false);
    const textNode = document.createTextNode('');
    body.classList.remove('dc-clamp');
    body.replaceChildren(textNode, toggle);

    let lo = 0;
    let hi = text.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      textNode.data = text.slice(0, mid).replace(/\s+$/, '');
      if (body.scrollHeight <= max + 1) lo = mid;
      else hi = mid - 1;
    }
    textNode.data = text.slice(0, lo).replace(/\s+$/, '');
    body.classList.add('dc-clamp');
    body.dataset.dcRendered = 'collapsed';
  }

  function clampReviewBodies() {
    if (ugcMobile.matches) return; // MO: 2 lines in CSS, the card opens the review
    document.querySelectorAll('#stamped-main-widget .stamped-review').forEach((review) => {
      const body = review.querySelector('.stamped-review-content-body');
      if (!body || !body.clientWidth) return; // hidden (other tab) — try later

      if (!review.dataset.dcLong) {
        if (body.dataset.dcClampChecked) return;
        body.dataset.dcClampChecked = 'true';
        if (body.scrollHeight <= maxHeightOf(body) + 1) return;
        review.dataset.dcLong = 'true';
        review.dcFullHTML = originalHTML(body);
        renderReview(review);
        return;
      }

      // Long review whose <p> Stamped rewrote (e.g. after its own "Read
      // more" loaded the full text): take the new markup as the original.
      if (!body.querySelector('.dc-review-more')) {
        review.dcFullHTML = originalHTML(body);
        renderReview(review);
      }
    });
  }

  // ---- Photo modal (Stamped .stamped-ugc-modal, Figma "리뷰 모달") ----
  // Thumbnails styled in dc-component-stamped-widget.css. Stamped switches
  // photos/reviews in place (same nodes, new content), and every change goes
  // through Stamped: a thumbnail clicks the matching photo in its
  // all-photos strip (.stamped-ugc-modal-list-photos, hidden).
  // PC: the open review's own photos (Stamped has them only as hidden
  // dots), 100px under the title, at most 4 with "+n" on the 4th; rebuilt
  // whenever the modal moves to another photo.
  // MO: one 200px swipe row of every review's photos (the strip's order),
  // so the next review's photos follow on from this one's. When a swipe
  // settles on a photo of another review, that photo is clicked in the
  // strip and Stamped shows that review below. Built once per modal and
  // kept (outside the caption Stamped rewrites); only the current mark and
  // position are updated.
  const UGC_MAX_THUMBS = 4;
  const ugcMobile = window.matchMedia('(max-width: 767.4999px)');

  // Stamped CDN resize (same URL form its own review images use).
  const ugcPhotoUrl = (url) => url.replace(/^\/\//, 'https://').replace(/\/uploads\//, '/tr:h-400:/uploads/');

  // Strip <li> → full-size photo URL (its data-bg is the small /thumb/ one).
  function ugcStripPhotoUrl(li) {
    const bg = li.dataset.bg || li.style.backgroundImage || '';
    const match = bg.match(/url\(["']?([^"')]+)["']?\)/);
    return match ? ugcPhotoUrl(match[1].replace('/uploads/photos/thumb/', '/uploads/photos/')) : '';
  }

  function centreThumb(row, thumb, behavior) {
    row.scrollTo({ left: thumb.offsetLeft - (row.clientWidth - thumb.offsetWidth) / 2, behavior });
  }

  function decorateUgcModal() {
    const modal = document.querySelector('.stamped-ugc-modal.open');
    if (!modal) return;
    if (ugcMobile.matches) {
      decorateUgcModalMobile(modal);
      return;
    }
    const allRow = modal.querySelector('.dc-ugc-thumbs--all');
    if (allRow) allRow.remove();
    const title = modal.querySelector('.stamped-ugc-modal-caption-title');
    if (!title) return;
    const dots = [...modal.querySelectorAll('.stamped-ugc-modal-image-wrapper ul li[data-image-url]')];
    const review = modal.dataset.reviewIndex;
    const active = Math.max(dots.findIndex((dot) => dot.classList.contains('active')), 0);
    const key = review + ':' + dots.length + ':' + active;

    const current = modal.querySelector('.dc-ugc-thumbs:not(.dc-ugc-thumbs--all)');
    if (current && current.dataset.key === key && current.previousElementSibling === title) return;
    if (current) current.remove();
    if (!dots.length) return;

    const row = document.createElement('div');
    row.className = 'dc-ugc-thumbs';
    row.dataset.key = key;
    dots.forEach((dot, i) => {
      const thumb = document.createElement('button');
      thumb.type = 'button';
      thumb.className = 'dc-ugc-thumb';
      thumb.setAttribute('aria-label', '写真 ' + (i + 1) + ' / ' + dots.length);
      thumb.setAttribute('aria-current', String(i === active));
      if (i === UGC_MAX_THUMBS - 1 && dots.length > UGC_MAX_THUMBS) {
        thumb.dataset.more = '+' + (dots.length - (UGC_MAX_THUMBS - 1));
      }
      const img = document.createElement('img');
      img.src = ugcPhotoUrl(dot.dataset.imageUrl);
      img.alt = '';
      thumb.appendChild(img);
      thumb.addEventListener('click', () => {
        if (i === active) return;
        const strip = modal.querySelector(
          `.stamped-ugc-modal-list-photos li[data-index-review="${review}"][data-index-photo="${i}"]`
        );
        (strip || dot).click();
      });
      row.appendChild(thumb);
    });
    title.after(row);
  }

  function decorateUgcModalMobile(modal) {
    const own = modal.querySelector('.dc-ugc-thumbs:not(.dc-ugc-thumbs--all)');
    if (own) own.remove();
    const wrapper = modal.querySelector('.stamped-ugc-modal-content-wrapper');
    const strip = [...modal.querySelectorAll('.stamped-ugc-modal-list-photos li')];
    if (!wrapper || !strip.length) return;
    const active = Math.max(strip.findIndex((li) => li.classList.contains('active')), 0);

    let row = wrapper.querySelector(':scope > .dc-ugc-thumbs--all');
    if (row && row.dcStrip && row.dcStrip.length === strip.length && row.dcStrip.every((li, i) => li === strip[i])) {
      // Same photos: move the mark; re-centre unless this change came
      // from a swipe that already left the photo in the middle.
      if (row.dcActive === active) return;
      row.dcActive = active;
      [...row.children].forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === active)));
      if (row.dcSettled !== active) centreThumb(row, row.children[active], 'instant');
      row.dcSettled = null;
      return;
    }
    if (row) row.remove();

    row = document.createElement('div');
    row.className = 'dc-ugc-thumbs dc-ugc-thumbs--all';
    row.dcStrip = strip;
    row.dcActive = active;
    strip.forEach((li, i) => {
      const thumb = document.createElement('button');
      thumb.type = 'button';
      thumb.className = 'dc-ugc-thumb';
      thumb.setAttribute('aria-label', li.getAttribute('aria-label') || '写真 ' + (i + 1));
      thumb.setAttribute('aria-current', String(i === active));
      const img = document.createElement('img');
      img.src = ugcStripPhotoUrl(li);
      img.alt = '';
      img.loading = 'lazy';
      thumb.appendChild(img);
      thumb.addEventListener('click', () => centreThumb(row, thumb, 'smooth'));
      row.appendChild(thumb);
    });
    wrapper.prepend(row);
    centreThumb(row, row.children[active], 'instant');

    // Stamped turns any horizontal touchmove on the page into next/previous
    // photo; keep the row's own swipe from reaching it.
    ['touchstart', 'touchmove'].forEach((type) =>
      row.addEventListener(type, (event) => event.stopPropagation(), { passive: true })
    );

    // When a swipe settles, show the review of the photo in the middle.
    let settleTimer = null;
    row.addEventListener(
      'scroll',
      () => {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => {
          const middle = row.scrollLeft + row.clientWidth / 2;
          let nearest = 0;
          [...row.children].forEach((thumb, i) => {
            const centre = thumb.offsetLeft + thumb.offsetWidth / 2;
            const best = row.children[nearest];
            if (Math.abs(centre - middle) < Math.abs(best.offsetLeft + best.offsetWidth / 2 - middle)) nearest = i;
          });
          if (nearest === row.dcActive) return;
          row.dcSettled = nearest;
          row.dcStrip[nearest].click();
        }, 150);
      },
      { passive: true }
    );
  }

  ugcMobile.addEventListener('change', () => {
    decorateUgcModal();
    clampReviewBodies();
    if (!ugcMobile.matches) closeSheet();
  });

  // ---- MO: tapping a review card opens the whole review ----
  // The card shows 2 lines (Figma 350:1856). A review with photos opens
  // Stamped's photo modal at its first photo (styled above for MO); one
  // without photos opens .dc-review-sheet, laid out the same way. Links and
  // the helpful votes keep their own action.
  let sheet = null;
  let sheetSource = null;

  function buildSheet() {
    sheet = document.createElement('div');
    sheet.className = 'dc-review-sheet';
    sheet.hidden = true;
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.innerHTML =
      '<button type="button" class="dc-review-sheet__close" aria-label="閉じる"></button>' +
      '<div class="dc-review-sheet__head"><span class="dc-review-sheet__user"></span><span class="dc-review-sheet__date"></span></div>' +
      '<div class="dc-review-sheet__stars"></div>' +
      '<h3 class="dc-review-sheet__title"></h3>' +
      '<div class="dc-review-sheet__body"></div>';
    sheet.querySelector('.dc-review-sheet__close').addEventListener('click', closeSheet);
    document.body.appendChild(sheet);
  }

  // Copy the review into the sheet (only when it changed, as writing the
  // sheet wakes the observer that calls this again).
  function fillSheet(review) {
    const text = (selector) => {
      const el = review.querySelector(selector);
      return el ? el.textContent.trim() : '';
    };
    const body = review.querySelector('.stamped-review-content-body');
    const holder = document.createElement('div');
    holder.innerHTML = review.dcFullHTML || (body ? originalHTML(body) : '');
    holder.querySelectorAll('a.stamped-review-read-more').forEach((el) => el.remove());
    const stars = review.querySelector('.stamped-review-header-starratings');
    const html = [
      text('.author'),
      review.dataset.verified,
      text('.created'),
      stars ? stars.innerHTML : '',
      text('.stamped-review-header-title'),
      holder.innerHTML,
    ];
    const key = JSON.stringify(html);
    if (sheet.dcKey === key) return;
    sheet.dcKey = key;

    const user = sheet.querySelector('.dc-review-sheet__user');
    user.textContent = html[0];
    user.toggleAttribute('data-verified', html[1] === 'buyer');
    sheet.querySelector('.dc-review-sheet__date').textContent = html[2];
    sheet.querySelector('.dc-review-sheet__stars').innerHTML = html[3];
    sheet.querySelector('.dc-review-sheet__title').textContent = html[4];
    sheet.querySelector('.dc-review-sheet__body').innerHTML = html[5];
    sheet.setAttribute('aria-label', html[4] || 'レビュー');
  }

  function openSheet(review) {
    if (!sheet) buildSheet();
    sheetSource = review;
    sheet.dcKey = null;
    fillSheet(review);
    sheet.hidden = false;
    sheet.scrollTop = 0;
    document.documentElement.classList.add('dc-review-sheet-open');
    sheet.querySelector('.dc-review-sheet__close').focus({ preventScroll: true });
    // Stamped cut the text at its word limit: load the rest (fillSheet
    // picks it up when Stamped rewrites the review).
    const stampedMore = review.querySelector('.stamped-review-content-body a.stamped-review-read-more');
    if (stampedMore) stampedMore.click();
  }

  function closeSheet() {
    if (!sheet || sheet.hidden) return;
    sheet.hidden = true;
    sheetSource = null;
    document.documentElement.classList.remove('dc-review-sheet-open');
  }

  document.addEventListener('click', (event) => {
    if (!ugcMobile.matches || !event.target.closest) return;
    const review = event.target.closest('#stamped-main-widget .stamped-review');
    if (!review || event.target.closest('a, button, .stamped-review-footer')) return;
    const photo = review.querySelector('.stamped-review-image a');
    if (photo) photo.click();
    else openSheet(review);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeSheet();
  });

  // Re-fit collapsed reviews when the width changes.
  let resizeTimer = null;
  let lastWidth = window.innerWidth;
  window.addEventListener('resize', () => {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      document
        .querySelectorAll('#stamped-main-widget .stamped-review[data-dc-long]:not([data-dc-expanded="true"])')
        .forEach(renderReview);
    }, 150);
  });

  // Moast reels above the reviews (the Moast carousel app block in the same
  // section). Figma has the prev arrow dimmed at the start, i.e. no looping;
  // Moast has no setting for that, and only reads it when it sets the slider
  // up (on first view, well after this runs), so it's set here.
  function getReels() {
    const widget = document.getElementById('stamped-main-widget');
    const section = widget && widget.closest('.shopify-section');
    return section && section.querySelector(':scope > .shopify-app-block > moast-carousel');
  }

  function setUpReels() {
    const reels = getReels();
    if (reels && !reels.hasAttribute('no-loop')) reels.setAttribute('no-loop', '');
  }

  function init() {
    const form = getForm();
    if (form) watch(form);
    setUpReels();
    localizeCopy();
    formatDates();
    markReviewPhotos();
    addPhotoArrows();
    clampReviewBodies();
    decorateUgcModal();
    if (sheetSource && sheet && !sheet.hidden) {
      if (sheetSource.isConnected) fillSheet(sheetSource);
      else closeSheet(); // Stamped re-rendered the list (page / sort change)
    }
  }

  // Stamped can (re)render the widget after load — pick up its form whenever
  // it appears (batched to one check per frame).
  let pending = false;
  new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      init();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
